import { type FC, memo, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import * as Yup from 'yup';
import { FormikProvider, useFormik } from 'formik';
import { isEmpty, isNil, keys, noop, sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Types
import type { InformationStepFormValues } from './types';
import type { StepProps } from 'components/common/Stepper/types';
import type { DropdownOption, ExclusionRulesListRowData, PartnerCustomField } from 'types';
// Import Enums
import { PageTypesEnum, PermissionEnum, ObjectTypeIdsEnum, CJStepsEnum, SubscriptionSectionEnum } from 'enums';
// Import Constants
import {
    l,
    FieldTypes,
    PARTNER_SETTINGS,
    MonthDayYearFormat,
    TimeZonePack,
    CustomerJourneyStatusLabels,
    TimeFormatWithoutSeconds,
    BLOCKED_BY_SUBSCRIPTION_CLASSNAME,
} from 'constants/common';
// Import Components
import {
    Checkbox,
    Col,
    Dropdown,
    ExtendedInput,
    Icon,
    Label,
    Row,
    Section,
    SkeletonLoader,
    Switcher,
    TimePicker,
    Tooltip,
    Alert,
} from '@geneui/components';
import AccessControl from 'components/common/AccessControl';
import BlockedBySubscriptionInfo from 'components/common/BlockedBySubscriptionInfo';
import ValidatableDatePickerInput from 'components/common/ValidatableDatePickerInput';
import StepperCustomFields from 'components/Campaign/CampaignSteps/GeneralStep/components/StepperCustomFields';
// Import Styles
import styles from './InformationStep.module.scss';
// Import Hooks
import { useStepNativeActions } from 'components/common/Stepper';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
import { useMetricsList } from 'hooks/useMetricsList';
import useHasPermission from 'hooks/UseHasPermission';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Store
import { useAppSelector } from 'store';
import { selectUserActivePartnerTimeZone } from 'store/slices/userData';
// Import Context
import { useCJConfig } from 'context/cjConfigContext';
// Import Helpers
import { customMoment, customMomentWithoutTimezoneConversion } from 'services/Helpers';
// Import Services
import { ExclusionRulesHttpService, UtilsHttpService } from 'services/http';
import PredefinedHttpService from 'services/http/predefined';
// Import Validations
import { InformationStepValidationSchema } from 'validators/schemas.validation';

export const normalizeDatePickerValue = (value: Nullable<string | Date>, partnerDateFormat: string) => {
    if (isNil(value)) return null;

    if (value instanceof Date) {
        return moment(value).format(MonthDayYearFormat);
    }

    const parsedValue = moment(value, [partnerDateFormat, MonthDayYearFormat, moment.ISO_8601], true);
    return parsedValue.isValid() ? parsedValue.format(MonthDayYearFormat) : null;
};

export const INFORMATION_STEP_DEFAULT_VALUES: InformationStepFormValues = {
    isPublic: false,
    isNonMarketing: false,
    isActiveStartDate: false,
    autoStartDate: null,
    autoStartTime: null,
    isActiveEndDate: false,
    autoEndDate: null,
    autoEndTime: null,
    timeZone: null,
    trackingPeriod: null,
    metricId: null,
    exclusionRules: [],
    customField: {},
    regions: [],
    categories: [],
};

const InformationStep: FC<StepProps> = ({ mode, deps, setStepValues, updateStepConfig, stepperSubmitCount }) => {
    const { t } = useTranslation();

    const {
        status,
        initialIsNonMarketingCJ,
        initialIsPublicCJ,
        defaultSchedule,
        defaultCustomFields,
        defaultExclusionRules,
        defaultEfficiencyMetric,
        pageMode,
        isPredefined,
        initialPredefinedOptions,
    } = useCJConfig();

    const isViewMode = pageMode === PageTypesEnum.view;

    const { doPostRequest, doGetRequest } = useRequest();

    const { hasSubscriptionSection } = useHasPermission();
    const { isMetricsLoading, metricsMap } = useMetricsList();

    const ActivePartnerTimeZone = useAppSelector(selectUserActivePartnerTimeZone) ?? 0;
    const { partnerDateFormat } = usePartnerDateFormats();

    const isGoalBlockedBySubscription = !hasSubscriptionSection(SubscriptionSectionEnum.EfficiencyMetricsTracking);
    const isNonStarted = !isNil(status) && +status === +CustomerJourneyStatusLabels.NonStarted;
    const isEfficiencyMetricReadOnly = isViewMode || (mode === PageTypesEnum.edit && !isNonStarted);

    const metricDropdownOptions = useMemo<DropdownOption[]>(
        () => Object.entries(metricsMap).map(([key, value]) => ({ value: Number(key), label: value })),
        [metricsMap],
    );

    const [customFields, setCustomFields] = useState<PartnerCustomField[]>([]);
    const [exclusionRulesData, setExclusionRulesData] = useState<ExclusionRulesListRowData[]>([]);
    const [dynamicValidationSchema, setDynamicValidationSchema] = useState(Yup.object);

    const [regionsData, setRegionsData] = useState<{ label: string; value: number }[]>([]);
    const [isLoadingRegions, setIsLoadingRegions] = useState(false);
    const [categoriesData, setCategoriesData] = useState<{ label: string; value: number }[]>([]);
    const [isLoadingCategories, setIsLoadingCategories] = useState(false);

    const { getPartnerCustomFieldsRequest, getExclusionRulesListRequest, getCategoriesRequest, getRegionsRequest } =
        useCancelableRequests({
            getPartnerCustomFieldsRequest: UtilsHttpService.getPartnerCustomFields,
            getExclusionRulesListRequest: ExclusionRulesHttpService.getExclusionRulesList,
            getCategoriesRequest: PredefinedHttpService.getCategories,
            getRegionsRequest: PredefinedHttpService.getRegions,
        });

    const initialValues: InformationStepFormValues = useMemo(
        () => ({
            ...INFORMATION_STEP_DEFAULT_VALUES,
            isNonMarketing: initialIsNonMarketingCJ ?? INFORMATION_STEP_DEFAULT_VALUES.isNonMarketing,
            isPublic: initialIsPublicCJ ?? INFORMATION_STEP_DEFAULT_VALUES.isPublic,
            timeZone: defaultSchedule?.TimeZone ?? ActivePartnerTimeZone,
            trackingPeriod: defaultEfficiencyMetric?.AnalyticsPeriod ?? INFORMATION_STEP_DEFAULT_VALUES.trackingPeriod,
            metricId: defaultEfficiencyMetric?.MetricId ?? INFORMATION_STEP_DEFAULT_VALUES.metricId,
            ...(defaultSchedule
                ? {
                      isActiveStartDate: defaultSchedule.isActiveStartDate ?? !isNil(defaultSchedule.StartDate),
                      autoStartDate: defaultSchedule.StartDate
                          ? customMomentWithoutTimezoneConversion(defaultSchedule.StartDate).format(MonthDayYearFormat)
                          : null,
                      autoStartTime: defaultSchedule.StartDate
                          ? customMomentWithoutTimezoneConversion(defaultSchedule.StartDate).format(
                                TimeFormatWithoutSeconds,
                            )
                          : null,
                      isActiveEndDate: defaultSchedule.isActiveEndDate ?? !isNil(defaultSchedule.EndDate),
                      autoEndDate: defaultSchedule.EndDate
                          ? customMomentWithoutTimezoneConversion(defaultSchedule.EndDate).format(MonthDayYearFormat)
                          : null,
                      autoEndTime: defaultSchedule.EndDate
                          ? customMomentWithoutTimezoneConversion(defaultSchedule.EndDate).format(
                                TimeFormatWithoutSeconds,
                            )
                          : null,
                  }
                : {}),
            ...(defaultCustomFields?.length && !isPredefined
                ? {
                      customField: defaultCustomFields.reduce<Record<string, string>>(
                          (acc, { CustomFieldId, FieldValue }) => {
                              acc[`id_${CustomFieldId}`] = FieldValue;
                              return acc;
                          },
                          {},
                      ),
                  }
                : {}),
            ...(defaultExclusionRules?.length ? { exclusionRules: defaultExclusionRules } : {}),
            ...(isPredefined && initialPredefinedOptions
                ? {
                      regions: initialPredefinedOptions.regions ?? [],
                      categories: initialPredefinedOptions.categories ?? [],
                  }
                : {}),
        }),
        [
            ActivePartnerTimeZone,
            initialIsNonMarketingCJ,
            initialIsPublicCJ,
            defaultSchedule,
            defaultCustomFields,
            defaultExclusionRules,
            defaultEfficiencyMetric,
            isPredefined,
            initialPredefinedOptions,
        ],
    );

    const journeyElements = useMemo(() => deps?.[CJStepsEnum.Journey]?.elements ?? [], [deps]);
    const budget = useMemo(() => deps?.[CJStepsEnum.BudgetLimit]?.budget, [deps]);

    const isEnabledBudgetLimit = !isEmpty(budget); // es ktore stugel vortev NoLimit tab chka el
    const minDate = customMoment().add(-1, 'days').format(MonthDayYearFormat);

    const isInProgress = !isNil(status) && +status === +CustomerJourneyStatusLabels.InProgress;
    const isVisibleStartDate = !isInProgress;
    const isVisibleEndDate = true;
    const staticValidationSchema = useMemo(
        () => InformationStepValidationSchema(t, journeyElements, isVisibleStartDate, isVisibleEndDate),
        [t, journeyElements, isVisibleStartDate, isVisibleEndDate],
    );

    const predefinedValidationSchema = useMemo(
        () =>
            isPredefined
                ? Yup.object({
                      regions: Yup.array()
                          .min(1, t(l.CannotBeEmpty, { fieldName: t(l.Regions) }))
                          .required(t(l.CannotBeEmpty, { fieldName: t(l.Regions) })),
                      categories: Yup.array()
                          .min(1, t(l.CannotBeEmpty, { fieldName: t(l.Categories) }))
                          .required(t(l.CannotBeEmpty, { fieldName: t(l.Categories) })),
                  })
                : Yup.object(),
        [t, isPredefined],
    );

    const formik = useFormik<InformationStepFormValues>({
        initialValues,
        enableReinitialize: true,
        validationSchema: staticValidationSchema.concat(dynamicValidationSchema).concat(predefinedValidationSchema),
        validateOnMount: true,
        onSubmit: noop,
    });

    const { values, setFieldValue, errors, touched, setFieldTouched, handleChange, handleBlur, validateForm } = formik;

    // Solves stepper async validation issue when stepper is mounted and formik validation was updated after initialization
    useEffect(() => {
        validateForm();
    }, [dynamicValidationSchema, validateForm]);

    const clearDefaults = useMemo(
        () => ({
            ...INFORMATION_STEP_DEFAULT_VALUES,
            exclusionRules: exclusionRulesData.map((rule) => rule.ExclusionRuleId),
        }),
        [exclusionRulesData],
    );

    const { increaseLoadProcessesCount, decreaseLoadProcessesCount } = useStepNativeActions({
        formik,
        defaultValues: clearDefaults,
        canClear: mode !== PageTypesEnum.view,
        canReset: ![PageTypesEnum.create, PageTypesEnum.view].includes(mode),
        setStepValues,
        updateStepConfig,
        stepperSubmitCount,
    });

    const handleToggleChange = (fieldName: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setFieldValue(fieldName, e.target.checked);

    const handleCheckboxChange =
        (fieldName: string) =>
        ({ target }: { target: { checked: boolean } }) => {
            setFieldTouched(fieldName, true, false);

            if (fieldName === 'isActiveStartDate') {
                if (target.checked) {
                    setFieldTouched('autoStartDate', true, false);
                    setFieldTouched('autoStartTime', true, false);
                }
            }
            if (fieldName === 'isActiveEndDate') {
                if (target.checked) {
                    setFieldTouched('autoEndDate', true, false);
                    setFieldTouched('autoEndTime', true, false);
                }
            }

            setFieldValue(fieldName, target.checked);
        };

    const handleDateChange = (fieldName: string) => (value: string | Date | null) => {
        setFieldTouched(fieldName, true, false);
        setFieldValue(fieldName, normalizeDatePickerValue(value, partnerDateFormat));
    };

    const handleTimeChange =
        (fieldName: string) =>
        ({ target }: { target: { value: string } }) => {
            setFieldTouched(fieldName, true, false);
            setFieldValue(fieldName, target.value);
        };

    const handleTimeZoneChange = (selectedTimeZone: { value: number }) => {
        setFieldValue('timeZone', selectedTimeZone?.value);
    };

    const handleMetricChange = (data: { value: number | null }) => setFieldValue('metricId', data?.value ?? null);

    const handleExclusionRulesToggleChange = (exclusionRuleId: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        const currentRules = values.exclusionRules;

        if (checked) {
            setFieldValue('exclusionRules', [...currentRules, exclusionRuleId]);
        } else {
            setFieldValue(
                'exclusionRules',
                currentRules.filter((id) => id !== exclusionRuleId),
            );
        }
    };

    useEffect(() => {
        increaseLoadProcessesCount();
        doPostRequest(getPartnerCustomFieldsRequest.request, {
            queryString: { objectType: ObjectTypeIdsEnum.RealtimePromotion },
            successCallback: (Data) => {
                setCustomFields(Data);

                const schema = Data.reduce((acc, field) => {
                    let validator = Yup.string().nullable();
                    if (field.IsRequired) {
                        validator = validator.required(t(l.CannotBeEmpty, { fieldName: field.Name }));
                    }
                    acc[`id_${field.CustomFieldId}`] = validator;
                    return acc;
                }, {});

                if (!isPredefined) {
                    setDynamicValidationSchema(
                        Yup.object({
                            [FieldTypes.CustomField]: Yup.object(schema),
                        }),
                    );
                }
            },
        })?.finally(() => {
            decreaseLoadProcessesCount();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        increaseLoadProcessesCount();
        doGetRequest(getExclusionRulesListRequest.request, {
            successCallback: (data: ExclusionRulesListRowData[]) => {
                const filteredRules = data.filter(({ ArchivedDate }) => isNil(ArchivedDate));
                setExclusionRulesData(filteredRules);
                if (mode === PageTypesEnum.create && !defaultExclusionRules?.length) {
                    setFieldValue(
                        'exclusionRules',
                        filteredRules.map((rule) => rule.ExclusionRuleId),
                    );
                }
            },
        }).finally(() => {
            decreaseLoadProcessesCount();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isPredefined) return;

        increaseLoadProcessesCount();
        setIsLoadingRegions(true);
        doGetRequest(getRegionsRequest.request, {
            successCallback: (data: { Name: string; PredefinedRegionId: number }[]) => {
                setRegionsData(data.map((item) => ({ label: item.Name, value: item.PredefinedRegionId })));
            },
        }).finally(() => {
            setIsLoadingRegions(false);
            decreaseLoadProcessesCount();
        });

        increaseLoadProcessesCount();
        setIsLoadingCategories(true);
        doGetRequest(getCategoriesRequest.request, {
            successCallback: (data: { Name: string; PredefinedCategoryId: number }[]) => {
                setCategoriesData(data.map((item) => ({ label: item.Name, value: item.PredefinedCategoryId })));
            },
        }).finally(() => {
            setIsLoadingCategories(false);
            decreaseLoadProcessesCount();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={styles.informationStep}>
            {!isPredefined && (
                <Section text={t(l.GoalSection)} color="base">
                    <BlockedBySubscriptionInfo isBlocked={isGoalBlockedBySubscription} align="start">
                        <div
                            className={classNames({
                                [BLOCKED_BY_SUBSCRIPTION_CLASSNAME]: isGoalBlockedBySubscription,
                                'not-clickable': isGoalBlockedBySubscription,
                            })}
                        >
                            <Row>
                                <Col size={6}>
                                    <ExtendedInput
                                        placeholder={t(l.TrackingPeriod)}
                                        labelAppearance="swap"
                                        pattern="[0-9]*"
                                        type="number"
                                        min={3}
                                        max={14}
                                        name="trackingPeriod"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.trackingPeriod}
                                        isValid={!touched.trackingPeriod || !errors.trackingPeriod}
                                        errorText={errors.trackingPeriod}
                                        disabled={isEfficiencyMetricReadOnly}
                                    />
                                </Col>
                                <Col size={6}>
                                    <Dropdown
                                        placeholder={t(l.Metric)}
                                        labelAppearance="swap"
                                        isMultiSelect={false}
                                        hasSearch={true}
                                        inputSize="default"
                                        appearance="outline"
                                        data={metricDropdownOptions}
                                        noDataText={t(l.NoDataFound)}
                                        onChange={handleMetricChange}
                                        value={values.metricId}
                                        isValid={!touched.metricId || !errors.metricId}
                                        errorText={errors.metricId}
                                        clearable={true}
                                        disabled={isEfficiencyMetricReadOnly}
                                        isLoading={isMetricsLoading}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </BlockedBySubscriptionInfo>
                </Section>
            )}
            {isPredefined && (
                <Section text={t(l.Predefined)} color="base">
                    <Row>
                        <Col size={6}>
                            <SkeletonLoader isBusy={isLoadingRegions}>
                                <Dropdown
                                    isMultiSelect={true}
                                    label={t(l.Regions)}
                                    placeholder={t(l.Regions)}
                                    labelAppearance="swap"
                                    data={regionsData}
                                    value={values.regions}
                                    onChange={(e) =>
                                        setFieldValue(
                                            'regions',
                                            e.map((selectedOption) => selectedOption.value),
                                        )
                                    }
                                    isLoading={isLoadingRegions}
                                    isValid={!errors.regions}
                                    errorText={errors.regions as string}
                                    disabled={isViewMode}
                                />
                            </SkeletonLoader>
                        </Col>
                        <Col size={6}>
                            <SkeletonLoader isBusy={isLoadingCategories}>
                                <Dropdown
                                    isMultiSelect={true}
                                    label={t(l.Categories)}
                                    placeholder={t(l.Categories)}
                                    labelAppearance="swap"
                                    data={categoriesData}
                                    value={values.categories}
                                    onChange={(e) =>
                                        setFieldValue(
                                            'categories',
                                            e.map((selectedOption) => selectedOption.value),
                                        )
                                    }
                                    isLoading={isLoadingCategories}
                                    isValid={!errors.categories}
                                    errorText={errors.categories as string}
                                    disabled={isViewMode}
                                />
                            </SkeletonLoader>
                        </Col>
                    </Row>
                </Section>
            )}

            <div className={styles.switcherContainer}>
                {(values.isPublic || isEnabledBudgetLimit) && (
                    <Alert
                        title={t(
                            isEnabledBudgetLimit
                                ? l.PublicPromotionDisabledReasonRelatedToBudgetLimit
                                : l.BudgetLimitNotApplicableForPublicPromotions,
                        )}
                        iconProps={{ type: 'bc-icon-info' }}
                        type="warning"
                    />
                )}
                <AccessControl partnerSetting={PARTNER_SETTINGS.IsAllowABTest}>
                    <div className={styles.switcherRow}>
                        <Switcher
                            checked={values.isPublic}
                            onChange={handleToggleChange('isPublic')}
                            label={t(l.PublicPromotion)}
                            disabled={isViewMode || values.isNonMarketing || isEnabledBudgetLimit}
                        />
                        <Tooltip text={t(l.PublicPromotionDescription)} position="right">
                            <Icon type="bc-icon-info" />
                        </Tooltip>
                    </div>
                </AccessControl>
                <AccessControl permissions={PermissionEnum.ManageNonMarketingCampaign}>
                    <div className={styles.switcherRow}>
                        <Switcher
                            checked={values.isNonMarketing}
                            onChange={handleToggleChange('isNonMarketing')}
                            label={t(l.IsNonMarketingPromotion)}
                            disabled={isViewMode || values.isPublic}
                        />
                        <Tooltip text={t(l.IsNonMarketingPromotionDescription)} position="right">
                            <Icon type="bc-icon-info" />
                        </Tooltip>
                    </div>
                </AccessControl>
            </div>

            {(isVisibleStartDate || isVisibleEndDate) && (
                <div className={styles.scheduleContainer}>
                    <div className={styles.titleWrapper}>
                        <p className={styles.titleText}>{t(l.Schedule)}</p>
                    </div>

                    <div className={styles.scheduleFieldsRow}>
                        {isVisibleStartDate && (
                            <div className={styles.scheduleFieldGroup}>
                                <div className={styles.scheduleFieldHeader}>
                                    <Checkbox
                                        label={t(l.AutoStart)}
                                        checked={values.isActiveStartDate}
                                        onChange={handleCheckboxChange('isActiveStartDate')}
                                        disabled={isViewMode}
                                    />
                                    <Tooltip text={t(l.CJAutoStartInputInfo)} position="right">
                                        <Icon type="bc-icon-info" />
                                    </Tooltip>
                                </div>
                                <div className={styles.scheduleFieldInputs}>
                                    <ValidatableDatePickerInput
                                        onChange={handleDateChange('autoStartDate')}
                                        onBlur={() => setFieldTouched('autoStartDate', true)}
                                        value={values.autoStartDate ?? undefined}
                                        placeholder={t(l.AutoStart)}
                                        min={minDate}
                                        isTouched={values.isActiveStartDate && !!touched.autoStartDate}
                                        isValid={!values.isActiveStartDate || !errors.autoStartDate}
                                        errorText={values.isActiveStartDate ? errors.autoStartDate : undefined}
                                        format={partnerDateFormat}
                                        disabled={isViewMode || !values.isActiveStartDate}
                                    />
                                    <TimePicker
                                        appearance="outline"
                                        value={values.autoStartTime}
                                        onChange={handleTimeChange('autoStartTime')}
                                        onBlur={() => setFieldTouched('autoStartTime', true)}
                                        showSeconds={false}
                                        isValid={!values.isActiveStartDate || !errors.autoStartTime}
                                        errorText={values.isActiveStartDate ? errors.autoStartTime : undefined}
                                        disabled={isViewMode || !values.isActiveStartDate}
                                    />
                                </div>
                            </div>
                        )}

                        {isVisibleEndDate && (
                            <div className={styles.scheduleFieldGroup}>
                                <div className={styles.scheduleFieldHeader}>
                                    <Checkbox
                                        label={t(l.AutoEnd)}
                                        checked={values.isActiveEndDate}
                                        onChange={handleCheckboxChange('isActiveEndDate')}
                                        disabled={isViewMode}
                                    />
                                    <Tooltip text={t(l.CJAutoEndInputInfo)} position="right">
                                        <Icon type="bc-icon-info" />
                                    </Tooltip>
                                </div>
                                <div className={styles.scheduleFieldInputs}>
                                    <ValidatableDatePickerInput
                                        onChange={handleDateChange('autoEndDate')}
                                        onBlur={() => setFieldTouched('autoEndDate', true)}
                                        value={values.autoEndDate ?? undefined}
                                        placeholder={t(l.AutoEnd)}
                                        min={minDate}
                                        isTouched={values.isActiveEndDate && !!touched.autoEndDate}
                                        isValid={!values.isActiveEndDate || !errors.autoEndDate}
                                        errorText={values.isActiveEndDate ? errors.autoEndDate : undefined}
                                        format={partnerDateFormat}
                                        disabled={isViewMode || !values.isActiveEndDate}
                                    />
                                    <TimePicker
                                        appearance="outline"
                                        value={values.autoEndTime}
                                        onChange={handleTimeChange('autoEndTime')}
                                        onBlur={() => setFieldTouched('autoEndTime', true)}
                                        showSeconds={false}
                                        isValid={!values.isActiveEndDate || !errors.autoEndTime}
                                        errorText={values.isActiveEndDate ? errors.autoEndTime : undefined}
                                        disabled={isViewMode || !values.isActiveEndDate}
                                    />
                                </div>
                            </div>
                        )}

                        <>
                            <span className={styles.scheduleFieldDivider} />
                            <div className={`${styles.scheduleFieldGroup} ${styles.scheduleFieldGroupNarrow}`}>
                                <div className={styles.scheduleFieldHeader}>
                                    <Label>{t(l.TimeZone)}</Label>
                                    <Tooltip text={t(l.CJAutoStartEndTimeZoneInfo)} position="right">
                                        <Icon type="bc-icon-info" />
                                    </Tooltip>
                                </div>
                                <div className={styles.scheduleFieldInputs}>
                                    <Dropdown
                                        flexibility="full-width"
                                        hasSearch={true}
                                        onChange={handleTimeZoneChange}
                                        value={values.timeZone}
                                        appearance="outline"
                                        disabled={isViewMode || (!values.isActiveStartDate && !values.isActiveEndDate)}
                                        data={sortBy(
                                            keys(TimeZonePack).map((key) => ({
                                                label: TimeZonePack[key],
                                                value: +key,
                                            })),
                                            ['value'],
                                        )}
                                        noDataText={t(l.NoDataFound)}
                                    />
                                </div>
                            </div>
                        </>
                    </div>
                </div>
            )}

            {!isEmpty(exclusionRulesData) && (
                <Section text={t(l.ExclusionRules)} color="base">
                    <Row>
                        {exclusionRulesData.map(({ Name, ExclusionRuleId }) => (
                            <Col size={4} key={ExclusionRuleId}>
                                <Switcher
                                    checked={values.exclusionRules.includes(ExclusionRuleId)}
                                    onChange={handleExclusionRulesToggleChange(ExclusionRuleId)}
                                    label={Name}
                                    disabled={isViewMode}
                                />
                            </Col>
                        ))}
                        <Col size={12}>
                            <Alert title={t(l.ExclusionRulesAlertMessage)} iconProps={{ type: 'bc-icon-info' }} />
                        </Col>
                    </Row>
                </Section>
            )}

            {!isEmpty(customFields) && (
                <Col size={12} className={styles.customFieldsContainer}>
                    <Section text={t(l.CustomFields)} color="base">
                        <Row>
                            <FormikProvider value={formik}>
                                <StepperCustomFields customFields={customFields} disabled={isViewMode} />
                            </FormikProvider>
                        </Row>
                    </Section>
                </Col>
            )}
        </div>
    );
};

export default memo(InformationStep);
